import React, { useEffect, useRef, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { checkCheckoutStatus } from "../lib/api";
import { CheckCircle, Clock, X, ArrowRight } from "@phosphor-icons/react";
import { Button } from "../components/ui/button";
import { useCart } from "../lib/cart";

const POLL_INTERVAL = 2000;
const MAX_ATTEMPTS = 6;

export default function Success() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState({ status: "checking", payment_status: null, data: null, attempts: 0 });
  const cleared = useRef(false);
  const { clearCart } = useCart();

  useEffect(() => {
    if (!sessionId) {
      setState({ status: "error", payment_status: null, data: null, attempts: 0 });
      return;
    }

    let attempts = 0;
    let timer = null;

    const poll = async () => {
      attempts += 1;
      try {
        const res = await checkCheckoutStatus(sessionId);
        if (res.payment_status === "paid") {
          setState({ status: "complete", payment_status: "paid", data: res, attempts });
          if (!cleared.current) {
            clearCart();
            cleared.current = true;
          }
          return;
        }
        if (res.status === "expired") {
          setState({ status: "expired", payment_status: res.payment_status, data: res, attempts });
          return;
        }
        if (attempts >= MAX_ATTEMPTS) {
          setState({ status: "timeout", payment_status: res.payment_status, data: res, attempts });
          return;
        }
        setState({ status: "pending", payment_status: res.payment_status, data: res, attempts });
        timer = setTimeout(poll, POLL_INTERVAL);
      } catch (e) {
        setState({ status: "error", payment_status: null, data: null, attempts });
      }
    };
    poll();

    return () => {
      if (timer) clearTimeout(timer);
    };
  }, [sessionId, clearCart]);

  const isSuccess = state.status === "complete";
  const isError = state.status === "error" || state.status === "expired";

  return (
    <div className="max-w-3xl mx-auto px-4 py-24" data-testid="success-page">
      <div className="border-2 border-zinc-950 p-8 sm:p-12 text-center">
        {state.status === "checking" || state.status === "pending" ? (
          <>
            <Clock size={64} weight="duotone" className="mx-auto mb-4 text-[#002FA7] animate-pulse" />
            <h1 className="font-heading font-black text-3xl sm:text-4xl tracking-tighter mb-3" data-testid="success-status-title">
              Confirming payment...
            </h1>
            <p className="font-mono text-sm text-zinc-500 uppercase tracking-wider">
              Attempt {state.attempts}/{MAX_ATTEMPTS}
            </p>
          </>
        ) : isSuccess ? (
          <>
            <CheckCircle size={64} weight="duotone" className="mx-auto mb-4 text-green-600" />
            <h1 className="font-heading font-black text-3xl sm:text-5xl tracking-tighter mb-3" data-testid="success-status-title">
              Order confirmed.
            </h1>
            <p className="text-zinc-700 mb-6">
              Thanks for your purchase. We'll dispatch your gear within 24 hours.
            </p>
            <div className="border-t border-b border-zinc-200 py-6 my-6 text-left">
              <div className="flex justify-between mb-2">
                <span className="font-mono uppercase text-xs tracking-[0.2em] text-zinc-500">Session</span>
                <span className="font-mono text-xs">{sessionId?.slice(0, 18)}...</span>
              </div>
              <div className="flex justify-between mb-4">
                <span className="font-mono uppercase text-xs tracking-[0.2em] text-zinc-500">Total</span>
                <span className="font-mono text-xl font-bold" data-testid="success-total">
                  ${state.data?.amount_total ? (state.data.amount_total / 100).toFixed(2) : "—"}
                </span>
              </div>
              {state.data?.items?.length > 0 && (
                <ul className="space-y-2 mt-4 text-sm">
                  {state.data.items.map((it) => (
                    <li key={it.product_id} className="flex justify-between font-mono">
                      <span>{it.name} × {it.quantity}</span>
                      <span>${(it.line_total).toFixed(2)}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <Link to="/products">
              <Button className="rounded-none bg-zinc-950 hover:bg-[#002FA7] text-white font-bold uppercase tracking-wider" data-testid="continue-shopping-button">
                Continue Shopping <ArrowRight size={16} weight="bold" className="ml-2" />
              </Button>
            </Link>
          </>
        ) : (
          <>
            <X size={64} weight="duotone" className="mx-auto mb-4 text-[#FF2A00]" />
            <h1 className="font-heading font-black text-3xl sm:text-4xl tracking-tighter mb-3" data-testid="success-status-title">
              {state.status === "expired" ? "Payment expired" : state.status === "timeout" ? "Verification timeout" : "Verification error"}
            </h1>
            <p className="text-zinc-700 mb-6">
              {state.status === "timeout"
                ? "Your payment may still be processing. Check your email for confirmation."
                : "Please try again or contact support if you were charged."}
            </p>
            <Link to="/products">
              <Button variant="outline" className="rounded-none border-2 border-zinc-950 font-bold uppercase tracking-wider">
                Back to shop
              </Button>
            </Link>
          </>
        )}
      </div>
    </div>
  );
}
